import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Media } from '../lib/types';
import { VideoPlayer } from './VideoPlayer';
import './Lightbox.css';

interface Props {
  /** Ordered media items to page through. */
  items: Media[];
  /** Index of the item shown on open. */
  index: number;
  /** Close the overlay (backdrop click, × button, or Esc). */
  onClose: () => void;
  /** Fires when the visible item changes (arrows / swipe / keys). */
  onIndexChange?: (i: number) => void;
}

const SWIPE_THRESHOLD = 56;

export function Lightbox({ items, index, onClose, onIndexChange }: Props) {
  const [current, setCurrent] = useState(index);
  const [zoomed, setZoomed] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const dragStartX = useRef<number | null>(null);
  const dragDelta = useRef(0);

  const count = items.length;

  // Keep internal index in sync if the caller changes `index` while open.
  useEffect(() => { setCurrent(index); }, [index]);

  const setIndex = useCallback(
    (i: number) => {
      const clamped = Math.max(0, Math.min(count - 1, i));
      setZoomed(false);
      setCurrent(clamped);
      onIndexChange?.(clamped);
    },
    [count, onIndexChange],
  );
  const prev = useCallback(() => setIndex(current - 1), [current, setIndex]);
  const next = useCallback(() => setIndex(current + 1), [current, setIndex]);

  // Body scroll lock while mounted.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  // Move focus into the overlay on open.
  useEffect(() => { closeRef.current?.focus(); }, []);

  // Keyboard: Esc closes, arrows page, Tab is trapped within the overlay.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); return; }
      if (e.key === 'ArrowLeft') { e.preventDefault(); prev(); return; }
      if (e.key === 'ArrowRight') { e.preventDefault(); next(); return; }
      if (e.key === 'Tab') {
        const root = overlayRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], video, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, prev, next]);

  function onBackdropClick(e: React.MouseEvent) {
    if (e.target === e.currentTarget) onClose();
  }

  function onPointerDown(e: React.PointerEvent) {
    dragStartX.current = e.clientX;
    dragDelta.current = 0;
  }
  function onPointerMove(e: React.PointerEvent) {
    if (dragStartX.current === null) return;
    dragDelta.current = e.clientX - dragStartX.current;
  }
  function onPointerEnd() {
    if (dragStartX.current === null) return;
    const delta = dragDelta.current;
    dragStartX.current = null;
    dragDelta.current = 0;
    if (zoomed) return; // don't page while zoomed
    if (delta <= -SWIPE_THRESHOLD) next();
    else if (delta >= SWIPE_THRESHOLD) prev();
  }

  if (count === 0) return null;
  const item = items[current];
  const isImage = item.kind === 'image';

  // Portal to <body> so the overlay escapes any ancestor stacking context
  // (e.g. the sticky lot gallery) and reliably sits above the whole page.
  return createPortal(
    <div
      ref={overlayRef}
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`Media ${current + 1} of ${count}`}
      onClick={onBackdropClick}
    >
      <button
        ref={closeRef}
        type="button"
        className="lightbox__close"
        onClick={onClose}
        aria-label="Close"
      >
        <CloseIcon />
      </button>

      {count > 1 && (
        <>
          <button
            type="button"
            className="lightbox__arrow lightbox__arrow--prev"
            onClick={prev}
            disabled={current === 0}
            aria-label="Previous"
          >
            <Chevron dir="left" />
          </button>
          <button
            type="button"
            className="lightbox__arrow lightbox__arrow--next"
            onClick={next}
            disabled={current === count - 1}
            aria-label="Next"
          >
            <Chevron dir="right" />
          </button>
        </>
      )}

      <div
        className="lightbox__stage"
        onClick={onBackdropClick}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
      >
        {isImage ? (
          <img
            className={`lightbox__image ${zoomed ? 'lightbox__image--zoomed' : ''}`}
            src={item.thumbnail_url ?? undefined}
            alt=""
            onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); }}
          />
        ) : (
          <div className="lightbox__video" onClick={(e) => e.stopPropagation()}>
            <VideoPlayer
              mediaId={item.id}
              poster={item.thumbnail_url}
              aspect={item.aspect_ratio ?? 16 / 9}
            />
          </div>
        )}
      </div>

      {count > 1 && (
        <p className="lightbox__counter" aria-live="polite">
          {current + 1} / {count}
        </p>
      )}
    </div>,
    document.body,
  );
}

function CloseIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d={dir === 'left' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
